import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Send, X, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkApplyBar = ({ 
  selectedProjects = [], // Array of {id, title, company_id, company_name}
  resource, // {id, first_name, last_name}
  onClearSelection
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);

  const getDefaultTemplate = () => {
    const resourceName = resource ? `${resource.first_name} ${resource.last_name}` : 'our consultant';
    return `Hello,

I would like to apply for your project "{projectTitle}" with ${resourceName}.

I believe this consultant would be a great match for your requirements.

Best regards`;
  };

  const handleOpenDialog = () => {
    setMessageTemplate(getDefaultTemplate());
    setResult(null);
    setShowDialog(true);
  };

  const handleApplyAll = async () => {
    setIsSending(true);
    const token = localStorage.getItem('token');
    
    const results = {
      success: [],
      failed: [],
      total: selectedProjects.length
    };

    for (const project of selectedProjects) {
      try {
        // Personalize message
        let personalizedMessage = messageTemplate
          .replace(/{projectTitle}/g, project.title || 'this project')
          .replace(/{companyName}/g, project.company_name || 'your company');

        // Send application
        await axios.post(`${API}/pre-reservations/apply`, null, {
          params: {
            project_id: project.id,
            resource_id: resource.id,
            message: personalizedMessage
          },
          headers: { Authorization: `Bearer ${token}` }
        });

        results.success.push(project);
      } catch (error) {
        console.error(`Failed to apply for ${project.title}:`, error);
        results.failed.push({
          project,
          error: error.response?.data?.detail || error.message
        });
      }
    }

    setResult({
      ...results,
      success_count: results.success.length,
      failed_count: results.failed.length
    });
    setIsSending(false);

    if (results.failed.length === 0) {
      toast.success(`Successfully applied to ${results.success_count} projects!`);
      setTimeout(() => {
        setShowDialog(false);
        onClearSelection();
      }, 2000);
    }
  };

  if (selectedProjects.length === 0) return null;

  const resourceName = resource ? `${resource.first_name} ${resource.last_name}` : 'Resource';

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
        <div 
          className="bg-white shadow-2xl rounded-lg border-2 border-green-500 px-6 py-4 flex items-center gap-4"
          style={{ minWidth: '500px' }}
        >
          <div className="flex-1">
            <p className="text-lg font-semibold" style={{ color: '#1B3240' }}>
              {selectedProjects.length} projects selected
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Ready to apply for multiple projects with {resourceName}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleOpenDialog}
              style={{ backgroundColor: '#62D84E' }}
              className="text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Apply to All
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={onClearSelection}
            >
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {result ? 'Application Results' : `Apply to ${selectedProjects.length} Projects`}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-900 mb-2">
                  📝 Personalized Applications
                </p>
                <p className="text-xs text-green-700">
                  Each project will receive a separate application with {resourceName}.
                  Available variables: <code className="bg-green-100 px-1 rounded">{`{projectTitle}`}</code>, 
                  <code className="bg-green-100 px-1 ml-1 rounded">{`{companyName}`}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Application Message Template
                </label>
                <Textarea
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  placeholder="Type your application message..."
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Preview: {selectedProjects.length} separate applications will be submitted
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {result.success_count > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      ✅ Successfully applied to {result.success_count} projects
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {result.success_count} applications submitted with {resourceName}
                    </p>
                  </div>
                </div>
              )}

              {result.failed_count > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    ❌ Failed to apply to {result.failed_count} projects
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {result.failed.map((failure, index) => (
                      <div key={index} className="text-xs text-red-700 py-1 border-t border-red-200">
                        {failure.project.title} - {failure.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDialog(false)} 
                  disabled={isSending}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleApplyAll} 
                  disabled={isSending || !messageTemplate.trim()}
                  style={{ backgroundColor: '#62D84E' }}
                  className="text-white"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Apply to {selectedProjects.length} Projects
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => {
                setShowDialog(false);
                if (result.failed_count === 0) {
                  onClearSelection();
                }
              }}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkApplyBar;
